/* eslint-disable @typescript-eslint/naming-convention */
import { LegacyRef, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Box,
    Button,
    Container,
    Flex,
    Heading,
    HStack,
    ScaleFade,
    Text,
    useBreakpointValue,
    useDisclosure,
    Wrap,
    WrapItem,
} from '@chakra-ui/react'
import {
    Active,
    DndContext,
    DragOverEvent,
    DragOverlay,
    Over,
    PointerSensor,
    pointerWithin,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { snapCenterToCursor } from '@dnd-kit/modifiers'
import { arrayMove } from '@dnd-kit/sortable'

import { MissingPlayersEditor } from '@components/MissingPlayersEditor/MissingPlayersEditor'

import { useFixtureLineup } from '@hooks/useFixtureLineup'

import {
    CurrentActivePlayer,
    FixtureLineup,
    MissingPlayerReason,
    MissingPlayerReasonAreaIds,
    MissingPlayersList,
    PlayerPosition,
    TMissingPlayerReasonAreaIds,
    TPlayer,
} from '@common/types'
import {
    deleteLineupFromCache,
    getLineupFromCache,
    getLineupsCache,
    removePlayerFromList,
    setLineupCache,
} from '@common/utils'

import { AddPlayerProvider } from '@contexts/AddPlayerContext'

import { LineupPreviewHeader } from '../LineupPreviewHeader'
import { NoSubstitutions } from '../NoSubstitutions'
import { PlayerBasicInfo } from '../PlayerBasicInfo'
import { FormationSelect } from './FormationSelect'
import { LineupEditor } from './LineupEditor'
import { LineupListEditor } from './LineupListEditor'
import { SquadList } from './SquadList'
import { SubstitutionsEditor } from './SubstitutionsEditor'
import { AddPlayerModal } from '@/components/LineupEditor/AddPlayerModal'
import { useLineupDataContext } from '@/contexts/LineupDataContext'

const getLineupClassName = (team: 'home' | 'away', isPossibleLineups: boolean) =>
    isPossibleLineups ? `${team}-team-possible-lineup` : `${team}-team-lineup`

const getSubsClassName = (team: 'home' | 'away') => `${team}-subs`

interface LineupEditModalContentProps {
    isHomeTeam: boolean
    isLineupWithoutFormation: boolean
    isPreviousLineup: boolean
    initialLineup?: FixtureLineup
    fixture: {
        team1: { id?: string | null; name?: string | null }
        team2: { id?: string | null; name?: string | null }
    }
    initialMissingPlayers?: MissingPlayersList
    isPossibleLineups: boolean
    initialIsPossibleLineups?: boolean
}

export type NewLineup = (TPlayer | null)[][]
export type NewSubs = TPlayer[] | any[]

const getTransformedLineup = (lineup: NewLineup, subs: NewSubs) => {
    const lineupPlayers = lineup.flat().map((player, index) => ({
        ...player,
        id: player?.id?.includes('front:fake') ? null : player?.id,
        order: index + 1,
        starter: true,
        captain: player?.captain ?? false,
    }))

    const subPlayers = subs
        ? subs
              .filter(player => player !== null)
              .map(player => ({
                  ...player,
                  captain: false,
                  id: player?.id?.includes('front:fake') ? null : player?.id,
              }))
        : []

    return lineupPlayers.concat(subPlayers).sort()
}

const setCachedLineup = (currentLineup: FixtureLineup, changes) => {
    const currentCacheJson = getLineupsCache()

    const key = currentLineup.fixture_id ?? ''

    const newCache = {
        ...currentCacheJson,
        [key]: {
            ...currentLineup,
            ...changes,
        },
    }

    setLineupCache(newCache)
}

const findPosition = (lineup, substitutions, playerId) => {
    const isSubstitute = playerId && !!substitutions.find(p => p && p.id === playerId)

    if (isSubstitute) {
        return {
            index: substitutions.findIndex(p => p && p.id === playerId),
        }
    }

    // eslint-disable-next-line no-plusplus
    for (let rowInd = 0; rowInd < lineup.length; rowInd++) {
        // eslint-disable-next-line no-plusplus
        for (let colInd = 0; colInd < lineup[rowInd].length; colInd++) {
            if (lineup[rowInd][colInd]?.id === playerId) {
                return { rowInd, colInd }
            }
        }
    }

    return null
}

export function LineupEditContainer({
    isHomeTeam,
    isLineupWithoutFormation,
    isPreviousLineup,
    initialLineup,
    fixture,
    initialMissingPlayers,
    isPossibleLineups,
    initialIsPossibleLineups,
}: LineupEditModalContentProps) {
    const [currentActivePlayer, setCurrentActivePlayer] = useState<CurrentActivePlayer | null>()
    const [currentEditPlayer, setCurrentEditPlayer] = useState<CurrentActivePlayer>()
    const [isExistingEditPlayer, setIsExistingEditPlayer] = useState<boolean>()

    const { lineupData, setLineupData } = useLineupDataContext()

    const { isOpen: isModalOpen, onClose: onCloseModal, onOpen: onOpenModal } = useDisclosure()
    const modalSize = useBreakpointValue({ base: 'full', md: 'md' })

    const { getTeamData } = useFixtureLineup(isHomeTeam ? 'team1' : 'team2')

    const [newSubs, setNewSubs] = useState<NewSubs>([])
    const [newFormation, setNewFormation] = useState<string | null>(null)
    const [newLineup, setNewLineup] = useState<NewLineup>([])
    const [newMissingPlayers, setNewMissingPlayers] = useState<MissingPlayersList | undefined>(initialMissingPlayers)
    const [isLineupEmpty, setIsLineupEmpty] = useState<boolean>(false)

    const teamId = isHomeTeam ? fixture?.team1.id : fixture?.team2.id

    const teamName = isHomeTeam ? fixture.team1.name : fixture.team2.name

    const modalRef = useRef<HTMLElement | null>(null)

    const [takenShirtNumbersLineup, setTakenShirtNumbersLineup] = useState<number[]>([])
    const [takenShirtNumbersSubs, setTakenShirtNumbersSubs] = useState<number[]>([])
    const [takenShirtNumbers, setTakenShirtNumbers] = useState<number[]>([])
    const [captainId, setCaptainId] = useState<string | null | undefined>()

    useEffect(() => {
        setNewMissingPlayers(initialMissingPlayers)
    }, [initialMissingPlayers])

    const checkIsEmptyLineup = lineup => lineup?.flat().filter(p => p !== null && p.name).length === 0

    const checkIsEmptyNumberLineup = lineup =>
        lineup?.flat().filter(p => p !== null && p.name && typeof p.shirt_number === 'number').length === 0

    const getUnsavedLineupChanges = (unsaved: { lineup?: NewLineup; subs?: NewSubs }) => {
        const unsavedLineup = unsaved.lineup ?? newLineup
        const unsavedSubs = unsaved.subs ?? newSubs

        const filteredSubs = unsavedSubs.filter(
            player =>
                !unsavedLineup
                    .flat()
                    .find(
                        lineupPlayer =>
                            (lineupPlayer?.id && lineupPlayer?.id === player?.id) ||
                            lineupPlayer?.name === player?.name,
                    ),
        )

        const unsavedTransformedLineup = getTransformedLineup(unsavedLineup, filteredSubs)

        return isHomeTeam ? { team1_lineup: unsavedTransformedLineup } : { team2_lineup: unsavedTransformedLineup }
    }

    const handleSubsChanges = (subs: NewSubs) => {
        setNewSubs(subs)

        setCachedLineup(lineupData, getUnsavedLineupChanges({ subs }))
    }

    const handleLineupChanges = (lineup: NewLineup, subs?: NewSubs) => {
        setNewLineup(lineup)

        setCachedLineup(lineupData, getUnsavedLineupChanges({ lineup, subs }))
    }

    const handleFormationChanges = (formation: string | null) => {
        setNewFormation(formation)

        setCachedLineup(lineupData, isHomeTeam ? { team1_formation: formation } : { team2_formation: formation })
    }

    useEffect(() => {
        const cachedLineup = lineupData?.fixture_id && getLineupFromCache(lineupData.fixture_id)

        const lineupInfo = getTeamData(cachedLineup || initialLineup)

        if (lineupInfo.formation !== null) {
            setNewLineup(lineupInfo?.lineup)
        } else {
            setNewLineup([
                ...lineupInfo.lineup,
                [...new Array(Math.max(11 - lineupInfo.lineup[0].length, 0)).fill(null)],
            ])
        }

        setNewSubs([
            ...lineupInfo.substitutions,
            ...new Array(Math.max(20 - lineupInfo.substitutions.length, 0)).fill(null),
        ])

        setNewFormation(lineupInfo?.formation)
    }, [initialLineup])

    useEffect(() => {
        const takenShirtNumbersArray: number[] = []

        const captain = newLineup.flat().find(player => player?.captain)
        setCaptainId(captain?.id)

        if (checkIsEmptyNumberLineup(newLineup)) {
            setTakenShirtNumbersLineup([])
        } else {
            newLineup.flat().forEach(player => {
                if (typeof player?.shirt_number === 'number') {
                    takenShirtNumbersArray.push(player?.shirt_number)

                    setTakenShirtNumbersLineup(takenShirtNumbersArray)
                }
            })
        }
    }, [newLineup])

    useEffect(() => {
        const takenShirtNumbersArray: number[] = []

        if (checkIsEmptyNumberLineup(newSubs)) {
            setTakenShirtNumbersSubs([])
        } else {
            newSubs.flat().forEach(subPlayer => {
                if (subPlayer?.shirt_number) {
                    takenShirtNumbersArray.push(subPlayer?.shirt_number)

                    setTakenShirtNumbersSubs(takenShirtNumbersArray)
                }
            })
        }
    }, [newSubs])

    useEffect(() => {
        if (isPossibleLineups) {
            setTakenShirtNumbers([...takenShirtNumbersLineup])

            return
        }

        setTakenShirtNumbers([...takenShirtNumbersLineup, ...takenShirtNumbersSubs])
    }, [takenShirtNumbersLineup, takenShirtNumbersSubs, isPossibleLineups])

    const areNumbersUnique = (numbers: number[]) => new Set(numbers).size !== numbers.length

    const isDuplicateShirtNumber = useMemo(() => areNumbersUnique(takenShirtNumbers), [takenShirtNumbers])

    useEffect(() => {
        if (isHomeTeam) {
            setLineupData(previousLineupData => ({
                ...previousLineupData,
                team1_lineup: getTransformedLineup(newLineup, newSubs),
                team1_formation: newFormation,
            }))
        } else {
            setLineupData(previousLineupData => ({
                ...previousLineupData,
                team2_lineup: getTransformedLineup(newLineup, newSubs),
                team2_formation: newFormation,
            }))
        }

        setIsLineupEmpty(checkIsEmptyLineup(newLineup) && checkIsEmptyLineup(newSubs))
    }, [newLineup, newSubs, newFormation])

    const handleAddPlayer = (playerData: PlayerPosition) => {
        if (playerData.position?.index !== undefined) {
            setSubstitutionPlayer(playerData.position.index, playerData.player)
        }

        if (playerData.position?.colIndex !== undefined && playerData.position?.rowIndex !== undefined) {
            setLineupPlayer(playerData.position.rowIndex, playerData.position.colIndex, playerData.player)
        }

        setIsExistingEditPlayer(false)

        onCloseModal()
    }

    const deleteLineup = () => {
        const lineup = newLineup.map(row =>
            row.map(() => ({
                starter: true,
            })),
        )

        const subs = newSubs.map(() => null)

        handleSubsChanges(subs)

        handleLineupChanges(lineup, [])
    }

    const filterSubs = (playerData, { index }) => {
        if (
            newSubs.find(
                subsPlayer =>
                    subsPlayer?.id === playerData?.player?.id || subsPlayer?.name === playerData?.player?.name,
            )
        ) {
            const currentSubsWithoutActivePlayer = [...newSubs.slice(0, index), null, ...newSubs.slice(index + 1)]

            handleSubsChanges(currentSubsWithoutActivePlayer)
        }
    }

    const extractBasicInfoFromPlayer = (player: TPlayer) => ({
        id: player.id,
        name: player.name,
        shirt_number: player.shirt_number,
        captain: player.captain,
    })

    const setLineupPlayer = (rowIndex?: number, colIndex?: number, player?: TPlayer) => {
        if (rowIndex === undefined || colIndex === undefined) {
            return
        }

        const currentLineup = [...newLineup]

        currentLineup[rowIndex][colIndex] = { ...(player && extractBasicInfoFromPlayer(player)), starter: true }

        handleLineupChanges(currentLineup)
    }

    const setSubstitutionPlayer = (index?: number, player?: TPlayer, subs?: any[]) => {
        if (index === undefined) {
            return
        }

        if (player?.captain) {
            player.captain = false
        }

        const currentSubs = subs ? [...subs] : [...newSubs]

        const playerIndexInSubs = currentSubs.findIndex(p => p && p.id === player?.id)
        if (playerIndexInSubs >= 0) {
            currentSubs[playerIndexInSubs] = currentSubs[index] || null
        }

        currentSubs[index] = player?.name
            ? { starter: false, order: null, ...(player && extractBasicInfoFromPlayer(player)) }
            : null

        handleSubsChanges(currentSubs)
    }

    const hasUnsavedChanges = useMemo(() => {
        const isPossibleLineupsChanged = Boolean(initialIsPossibleLineups) !== Boolean(isPossibleLineups)

        return Boolean(
            (lineupData?.fixture_id && getLineupFromCache(lineupData.fixture_id)) || isPossibleLineupsChanged,
        )
    }, [
        getLineupFromCache(lineupData?.fixture_id),
        isPossibleLineups,
        initialIsPossibleLineups,
        lineupData?.fixture_id,
    ])

    function getMissingPlayerReasonId(missingPlayerReasonArea: TMissingPlayerReasonAreaIds) {
        switch (missingPlayerReasonArea) {
            case MissingPlayerReasonAreaIds[MissingPlayerReason.MISSINGPLAYERREASON_INJURY]:
                return MissingPlayerReason.MISSINGPLAYERREASON_INJURY

            case MissingPlayerReasonAreaIds[MissingPlayerReason.MISSINGPLAYERREASON_SUSPENSION]:
                return MissingPlayerReason.MISSINGPLAYERREASON_SUSPENSION

            case MissingPlayerReasonAreaIds[MissingPlayerReason.MISSINGPLAYERREASON_DOUBTFUL]:
                return MissingPlayerReason.MISSINGPLAYERREASON_DOUBTFUL

            default:
                return null
        }
    }

    function handleMissingPlayersSortable(over: Over | null, active: Active) {
        const sortableOverContainerId = over?.data?.current?.sortable?.containerId || over?.id

        const sortableActiveContainerId = active?.data?.current?.sortable?.containerId

        const activeContainerReasonId = getMissingPlayerReasonId(sortableActiveContainerId)
        const overContainerReasonId = getMissingPlayerReasonId(sortableOverContainerId)

        if (!sortableOverContainerId || sortableOverContainerId !== sortableActiveContainerId) {
            return
        }

        const activeIndex = ((activeContainerReasonId && newMissingPlayers?.[activeContainerReasonId]) ?? []).findIndex(
            task => task.name === active.id,
        )

        const overIndex = ((overContainerReasonId && newMissingPlayers?.[overContainerReasonId]) ?? []).findIndex(
            task => task.name === over?.id,
        )

        if (activeIndex !== overIndex) {
            setNewMissingPlayers(prevMissingPlayers => ({
                ...prevMissingPlayers,
                ...(overContainerReasonId && {
                    [overContainerReasonId]: arrayMove(
                        (overContainerReasonId && prevMissingPlayers?.[overContainerReasonId]) ?? [],
                        activeIndex,
                        overIndex,
                    ),
                }),
            }))
        }
    }

    function handleDragEnd(event) {
        setCurrentActivePlayer(null)

        if (event.over) {
            const positionData = event.over.data.current
            let playerData = event.active.data.current

            if (
                !playerData.position ||
                playerData.position.colInd === undefined ||
                playerData.position.rowInd === undefined
            ) {
                const lineupPlayer = newLineup.flat().find(p => p?.id === event.active.data.current.player.id)
                playerData = lineupPlayer ?? playerData

                const playerId = playerData?.id ?? playerData.player?.id
                const isSubstitute = playerId && !!newSubs.find(p => p && p.id === playerId)

                if (!playerData.player) {
                    playerData = {
                        player: playerData,
                        position: findPosition(newLineup, newSubs, playerId),
                        isSubstitution: isSubstitute,
                    }
                } else {
                    playerData.position = findPosition(newLineup, newSubs, playerId)

                    playerData.isSubstitution = isSubstitute
                }
            }

            const currentLineup = [...newLineup]
            const currentSubs = [...newSubs]

            const sortableOverContainerId = positionData.sortable?.containerId || event.over?.id
            const sortableActiveContainerId = playerData?.sortable?.containerId

            if (Object.values(MissingPlayerReasonAreaIds).includes(sortableOverContainerId)) {
                handleMissingPlayersSortable(event.over, event.active)

                // player is being dropped in sortable container
                // but it's not coming from a sortable container (Lineups or Subs)
                // and it's not being dropped inside a doubtful container

                if (
                    !sortableActiveContainerId &&
                    sortableOverContainerId !==
                        MissingPlayerReasonAreaIds[MissingPlayerReason.MISSINGPLAYERREASON_DOUBTFUL]
                ) {
                    setLineupPlayer(playerData?.position?.rowInd, playerData?.position?.colInd)

                    setSubstitutionPlayer(playerData?.position?.index)
                }

                return
            }

            if (
                Object.values(MissingPlayerReasonAreaIds).includes(sortableActiveContainerId) &&
                sortableActiveContainerId !==
                    MissingPlayerReasonAreaIds[MissingPlayerReason.MISSINGPLAYERREASON_DOUBTFUL]
            ) {
                setNewMissingPlayers(removePlayerFromList(newMissingPlayers, playerData?.player?.name))
            }

            const playerOnTheSamePositionInLineup = currentLineup
                .map((row, rowIndex) =>
                    row.map((player, colIndex) =>
                        rowIndex === positionData.rowInd && colIndex === positionData.colInd ? player : null,
                    ),
                )
                .flat()
                .find(player => player !== null && player.id)

            const playerOnTheSamePositionInSubs = currentSubs.find(
                (player, index) => index === positionData.index && player !== null,
            )

            const playerIsNotComingFromSquad = playerData?.player?.starter !== undefined

            // Set new position for the dropped player

            if (positionData.isSubstitution) {
                setSubstitutionPlayer(positionData.index, playerData?.player, currentSubs)
            } else {
                setLineupPlayer(positionData.rowInd, positionData.colInd, playerData?.player)

                if (playerData.isSubstitution) {
                    filterSubs(playerData, playerData.position)
                }
            }

            if (playerData.initialPlacement === 'lineup' || playerData.initialPlacement === 'subs') {
                // if dropped over lineup player, do swaps
                if (playerOnTheSamePositionInLineup) {
                    setSubstitutionPlayer(playerData?.position?.index, playerOnTheSamePositionInLineup)

                    if (playerIsNotComingFromSquad) {
                        setLineupPlayer(
                            playerData?.position?.rowInd,
                            playerData?.position?.colInd,
                            playerOnTheSamePositionInLineup,
                        )
                    }
                }

                // if dropped over sub player, do swaps
                if (playerOnTheSamePositionInSubs) {
                    if (!playerData.isSubstitution && playerIsNotComingFromSquad) {
                        setLineupPlayer(
                            playerData?.position?.rowInd,
                            playerData?.position?.colInd,
                            playerOnTheSamePositionInSubs,
                        )
                    }

                    if (playerData.isSubstitution) {
                        setSubstitutionPlayer(playerData?.position?.index, playerOnTheSamePositionInSubs, currentSubs)
                    }
                }
            }

            if (
                currentLineup
                    .flat()
                    .find(
                        lineupPlayer =>
                            lineupPlayer?.id === playerData?.player?.id ||
                            lineupPlayer?.name === playerData?.player?.name,
                    )
            ) {
                const currentLineupWithoutActivePlayer = currentLineup.map((lineupRow, rowIndex) =>
                    lineupRow.map((player, columnIndex) =>
                        (player?.id === playerData.player?.id || player?.name === playerData.player?.name) &&
                        (positionData.rowInd !== rowIndex || positionData.colInd !== columnIndex)
                            ? null
                            : player,
                    ),
                )

                handleLineupChanges(currentLineupWithoutActivePlayer)
            }
        }
    }

    function handleDragStart(event) {
        setCurrentActivePlayer(event.active.data.current?.player)
    }

    function handleDragOver({ active, over }: DragOverEvent) {
        const sortableOverContainerId = over?.data?.current?.sortable?.containerId || over?.id
        const sortableActiveContainerId = active?.data?.current?.sortable?.containerId

        if (!Object.values(MissingPlayerReasonAreaIds).includes(sortableOverContainerId)) {
            if (over === null && !sortableActiveContainerId) {
                setNewMissingPlayers(prevMissingPlayers => {
                    if (!prevMissingPlayers) {
                        return {}
                    }

                    const activePlayerInMissingPlayers = Object.values(prevMissingPlayers)
                        .flat()
                        .find(
                            item =>
                                item.id === active?.data?.current?.player?.id &&
                                item.name === active?.data?.current?.player?.name,
                        )

                    return removePlayerFromList(prevMissingPlayers, activePlayerInMissingPlayers?.name)
                })
            }

            return
        }

        const activeContainerReasonId = getMissingPlayerReasonId(sortableActiveContainerId)
        const overContainerReasonId = getMissingPlayerReasonId(sortableOverContainerId)

        if (!sortableOverContainerId || sortableOverContainerId === sortableActiveContainerId) {
            return
        }

        setNewMissingPlayers(prevMissingPlayers => {
            const activeContainerList = (activeContainerReasonId && prevMissingPlayers?.[activeContainerReasonId]) ?? []

            const currentActivePlayerData = active?.data?.current?.player

            // we want the type to be changed to default (0) only if it's moved to/from suspensions list
            const isSuspensionContainerInvolved =
                overContainerReasonId === MissingPlayerReason.MISSINGPLAYERREASON_SUSPENSION ||
                activeContainerReasonId === MissingPlayerReason.MISSINGPLAYERREASON_SUSPENSION

            const activePlayerData = {
                ...extractBasicInfoFromPlayer(currentActivePlayerData),
                ...{
                    source: 'manual',
                    reason: overContainerReasonId,
                    type: isSuspensionContainerInvolved ? 0 : currentActivePlayerData?.type,
                },
            }

            const missingPlayersWithoutActive = removePlayerFromList(prevMissingPlayers, activePlayerData?.name)

            const overContainerListWithoutActive =
                (overContainerReasonId && missingPlayersWithoutActive[overContainerReasonId]) ?? []

            return {
                ...missingPlayersWithoutActive,
                ...(activeContainerReasonId && {
                    [activeContainerReasonId]: [...activeContainerList.filter(item => item.name !== active?.id)],
                }),
                ...(overContainerReasonId && {
                    [overContainerReasonId]: [activePlayerData, ...overContainerListWithoutActive],
                }),
            }
        })
    }

    const alertText = isLineupWithoutFormation
        ? {
              title: 'No formation provided!',
              description: 'This is the default formation. Please check the players!',
          }
        : {
              title: 'Previous match lineup!',
              description: 'This lineup is from the previous match. Please check the players!',
          }

    const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    const sensors = useSensors(pointerSensor)

    const handleEditPlayer = player => {
        setIsExistingEditPlayer(true)

        setCurrentEditPlayer({ ...player.player, ...player.position })

        onOpenModal()
    }

    const handleAddNewPlayer = position => {
        setCurrentEditPlayer(position)

        onOpenModal()
    }

    const discardChanges = () => {
        if (lineupData.fixture_id) {
            deleteLineupFromCache(lineupData.fixture_id)
        }

        const lineupInfo = getTeamData(initialLineup)

        setNewLineup(lineupInfo?.lineup)

        setNewSubs([
            ...lineupInfo.substitutions,
            ...new Array(Math.max(20 - lineupInfo.substitutions.length, 0)).fill(null),
        ])

        setNewFormation(lineupInfo?.formation)
    }

    const isSubstitute = !!currentEditPlayer && !!currentEditPlayer.index

    return (
        <DndContext
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            sensors={sensors}
            onDragOver={handleDragOver}
            collisionDetection={pointerWithin}>
            <Container maxW="container.xxl" p={{ base: 0, xl: 4 }}>
                {(isLineupWithoutFormation || isPreviousLineup) && (
                    <Alert status="warning" m={4}>
                        <AlertIcon />
                        <Box>
                            <AlertTitle>{alertText.title}</AlertTitle>
                            <AlertDescription>{alertText.description}</AlertDescription>
                        </Box>
                    </Alert>
                )}

                {isPossibleLineups ? (
                    <Heading as="h2" size="md" mb={1} minH="10">
                        Create New Possible Lineup
                    </Heading>
                ) : (
                    <Heading key="normal" as="h2" size="md" mb={1} minH="10">
                        Create New Lineup
                    </Heading>
                )}

                <Flex gap={4}>
                    <Box flex={{ base: 1, lg: 2 }} m={0}>
                        <Box
                            borderStyle="solid"
                            borderWidth="1px"
                            bg={isPossibleLineups ? 'sp.background' : 'white'}
                            transition={'background-color 0.5s, border-color 1s'}
                            borderRadius="16"
                            py={0}
                            px={{ base: 4, xl: 8 }}
                            ref={modalRef as LegacyRef<HTMLDivElement>}>
                            <HStack
                                mb={4}
                                position="sticky"
                                top="0"
                                zIndex={30}
                                py={4}
                                bg={isPossibleLineups ? 'sp.background' : 'white'}>
                                <FormationSelect
                                    newFormation={newFormation}
                                    currentLineup={newLineup}
                                    handleLineupChanges={handleLineupChanges}
                                    handleFormationChanges={handleFormationChanges}
                                />
                                <Button
                                    colorScheme="red"
                                    isDisabled={isLineupEmpty}
                                    onClick={deleteLineup}
                                    className="delete-lineup-players-button">
                                    Delete
                                </Button>
                                <Button
                                    className="discard-changes-button"
                                    colorScheme="grey"
                                    variant="outline"
                                    onClick={discardChanges}
                                    isDisabled={!hasUnsavedChanges}>
                                    Discard
                                </Button>
                            </HStack>
                            {hasUnsavedChanges && (
                                <Alert status="warning" mb={4} className="unsaved-changes-alert">
                                    <AlertIcon />
                                    <Box>
                                        <AlertDescription>You have unsaved changes!</AlertDescription>
                                    </Box>
                                </Alert>
                            )}
                            {isDuplicateShirtNumber && (
                                <Alert status="error" my={4} className="duplicate-shirt-number-alert">
                                    <AlertIcon />
                                    Duplicate shirt numbers!
                                </Alert>
                            )}
                            <Wrap alignItems="flex-start" spacing={4} justify="center" mb={8}>
                                <WrapItem
                                    flex={1}
                                    display="flex"
                                    flexDir="column"
                                    className={getLineupClassName(isHomeTeam ? 'home' : 'away', isPossibleLineups)}>
                                    <LineupPreviewHeader teamId={teamId} teamName={teamName} />
                                    {newFormation ? (
                                        <LineupEditor
                                            currentActivePlayer={currentActivePlayer}
                                            lineup={newLineup}
                                            formation={newFormation}
                                            isHomeTeam={isHomeTeam}
                                            handleEditPlayer={handleEditPlayer}
                                            handleAddPlayer={handleAddNewPlayer}
                                            handleLineupChanges={handleLineupChanges}
                                            takenShirtNumbers={takenShirtNumbers}
                                            isPossibleLineups={isPossibleLineups}
                                        />
                                    ) : (
                                        <LineupListEditor
                                            currentActivePlayer={currentActivePlayer}
                                            lineup={newLineup}
                                            isHomeTeam={isHomeTeam}
                                            handleEditPlayer={handleEditPlayer}
                                            handleAddPlayer={handleAddNewPlayer}
                                            handleLineupChanges={handleLineupChanges}
                                            showNoFormation={true}
                                            takenShirtNumbers={takenShirtNumbers}
                                            isPossibleLineup={isPossibleLineups}
                                        />
                                    )}
                                </WrapItem>
                                <WrapItem
                                    flex={1}
                                    display="flex"
                                    flexDir="column"
                                    className={getSubsClassName(isHomeTeam ? 'home' : 'away')}>
                                    <Text fontSize="sm" w="full" textAlign="end" mt="4" mb="5">
                                        Subs
                                    </Text>
                                    {newFormation !== undefined ? (
                                        <SubstitutionsEditor
                                            substitutions={newSubs}
                                            isHomeTeam={isHomeTeam}
                                            currentActivePlayer={currentActivePlayer}
                                            handleEditPlayer={handleEditPlayer}
                                            handleAddPlayer={handleAddNewPlayer}
                                            handleSubsChanges={handleSubsChanges}
                                            takenShirtNumbers={takenShirtNumbers}
                                            isPossibleLineup={isPossibleLineups}
                                        />
                                    ) : (
                                        <NoSubstitutions message="Select formation to start" />
                                    )}
                                </WrapItem>
                            </Wrap>
                        </Box>
                        <MissingPlayersEditor
                            takenShirtNumbers={takenShirtNumbers}
                            missingPlayers={newMissingPlayers}
                            setMissingPlayers={setNewMissingPlayers}
                            currentActivePlayer={currentActivePlayer}
                            fixture={fixture}
                            isHomeTeam={isHomeTeam}
                        />
                    </Box>
                    <Box
                        flex={1}
                        py={5}
                        px={{ base: 4, xl: 8 }}
                        borderStyle="solid"
                        borderWidth="1px"
                        borderColor="blackAlpha.50"
                        bg={isPossibleLineups ? 'sp.background' : 'white'}
                        transition={'background-color 0.5s, border-color 1s'}
                        borderRadius="16">
                        <SquadList
                            isHomeTeam={isHomeTeam}
                            teamId={teamId}
                            currentActivePlayer={currentActivePlayer}
                            isPossibleLineup={isPossibleLineups}
                        />
                    </Box>
                </Flex>
                <AddPlayerProvider
                    takenShirtNumbers={takenShirtNumbers}
                    onAddNewPlayer={handleAddPlayer}
                    enableCaptainEdit={!captainId || (currentEditPlayer && currentEditPlayer.id === captainId)}
                    currentActivePlayer={currentEditPlayer}
                    isExistingPlayer={isExistingEditPlayer}
                    isSubstitute={isSubstitute}>
                    <AddPlayerModal isOpen={isModalOpen} onClose={onCloseModal} size={modalSize} />
                </AddPlayerProvider>
            </Container>
            <DragOverlay modifiers={[snapCenterToCursor]}>
                <ScaleFade in={!!currentActivePlayer} initialScale={0.8} reverse={true}>
                    <PlayerBasicInfo player={currentActivePlayer} isHomeTeam={isHomeTeam} isDragOverlay={true} />
                </ScaleFade>
            </DragOverlay>
        </DndContext>
    )
}
